import {
	DocumentsViewModel,
	DocumentsViewType,
	SharePointColumnsVisibilityModel,
	Permissions,
	SearchScope
} from "client/Api/Documents/SharePoint/common";
import { Folderish } from "client_legacy/Documents/SharePoint/models";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { FolderNavigationStackItem } from "./useFolderNavigationStack";
import { SharePointBreadCrumb } from "./SharePointBreadCrumb";
import {
	GridItem,
	columnIdToNameMapping,
	SharePointOverView
} from "./SharePointOverView";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { DocumentPageLayout } from "../DocumentPageLayout";
import { SharePointFilter, SharePointSearchForm } from "./SharePointSearchForm";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	AlertBar,
	Anchor,
	ContextActionBar,
	DropZone,
	getPropertySortText,
	OptionsMenu,
	OptionsMenuItem,
	PageContentTitle,
	ToolbarAction
} from "client/Components";
import { SyncClient, TemplateAvailabilityKind } from "client/Api/SharePointSettings/sharepointSettings";
import { localize } from "client_legacy/ndaw/culture";
import {
	ColumnInitializationModel,
	ColumnVisibilityState,
	SharePointColumnsVisibility
} from "./SharePointColumnsVisibility/SharePointColumnsVisibility";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { ActionsWithContext } from "client_legacy/ndaw/actions";
import { SharePointDocumentActionContext } from "./actions";
import {
	EMPTY_ARRAY,
	isNotNil
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { DocumentSelect, DocumentSelectHandle } from "../DocumentSelect/DocumentSelect";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { doesServerSupportChunking } from "client/Api/Documents/SharePoint/sharePointFileContent";
import styles from "./SharePointPageContent.module.less";
import { UploadBatchHandlers } from "../DocumentUploader/useDocumentUploader";
import { useSharePointDocumentUploaderHandlers } from "./useSharePointDocumentUploaderHandlers";

export type SharePointPageContentProps = {
	isMobile: boolean;
	isResponsive: boolean;
	view: DocumentsViewModel;
	folderPath: readonly FolderNavigationStackItem[];
	onOpenFolder: (folder: Folderish) => void;
	onFolderPermissionsLoaded: (viewId: string, folderId: number | null, permissions: Permissions) => void;
	initialColumnConfiguration: SharePointColumnsVisibilityModel;
	isSharingViaLinkEnabled: boolean;
	maxNumberOfFilesToDownloadAsZip: number;
	syncClient: SyncClient;
	templateAvailabilityKind: TemplateAvailabilityKind;
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
	onDocumentUpload: (
		files: readonly FileSelectionEntry[],
		handlers: UploadBatchHandlers
	) => void;
};

export const SharePointPageContent = (props: SharePointPageContentProps): ReactElement => {
	const {
		isMobile,
		isResponsive,
		view,
		folderPath,
		onOpenFolder,
		onFolderPermissionsLoaded,
		initialColumnConfiguration,
		isSharingViaLinkEnabled,
		maxNumberOfFilesToDownloadAsZip,
		syncClient,
		templateAvailabilityKind,
		layoutKind,
		onLayoutKindChange,
		onDocumentUpload
	} = props;

	const [doesSupportChunking, setDoesSupportChunking] = useState<boolean>(false);

	const [columnVisibilities, setColumnVisibilities] = useState<readonly ColumnVisibilityState[]>(
		() => {
			return initialColumnConfiguration.columns
				.map((x): ColumnVisibilityState => ({
					columnId: x.id,
					isVisible: x.isVisible
				}));
		}
	);

	const viewKind: DocumentsViewType = view.type;
	if (viewKind === DocumentsViewType.RecycleBinView || viewKind === DocumentsViewType.RecentlyModified) {
		throw new Error("Cannot be a recycle bin or recently modified view");
	}

	const [sortingState, setSortingState] = useState<DataSourceSortableState<GridItem>>({
		propertyName: "name",
		direction: DataSourceSortDirection.Ascending
	});

	const [filter, setFilter] = useState<SharePointFilter>({
		scope: SearchScope.Subfolders,
		text: {
			value: "",
			version: newGuid()
		}
	});

	const [actionsWithContext, setActionsWithContext] = useState<SharePointActionsWithContext | null>(null);

	const documentSelectRef: RefObject<DocumentSelectHandle | null> = useRef<DocumentSelectHandle>(null);

	const folder = useMemo(
		(): Folderish | undefined => {
			return folderPath[folderPath.length - 1]?.folder;
		},
		[folderPath]
	);

	const uploadHandlers: UploadBatchHandlers | undefined = useSharePointDocumentUploaderHandlers(
		view,
		folder,
		doesSupportChunking,
		onOpenFolder
	);

	const columnsVisibilityDefinitions = useMemo(
		(): readonly ColumnInitializationModel[] => {
			return initialColumnConfiguration.columns
				.map((x): ColumnInitializationModel => {
					return {
						id: x.id,
						name: localize(columnIdToNameMapping[x.id]),
						isVisibleByDefault: x.isVisibleByDefault,
						cannotBeHidden: x.cannotBeHidden
					};
				});
		},
		[initialColumnConfiguration.columns]
	);

	const backHandler = useCallback(
		(item: FolderNavigationStackItem): void => {
			onOpenFolder(item.folder);
		},
		[onOpenFolder]
	);

	useEffect(
		() => {
			hasImplicitDependencyOn(view.id, folder?.id);

			setFilter(x => ({
				...x,
				text: {
					value: "",
					version: newGuid()
				}
			}));
		},
		[
			view.id,
			folder?.id
		]
	);

	const actionsChangeHandler = useCallback(
		(value: SharePointActionsWithContext): (() => void) => {
			setActionsWithContext(value);
			return () => setActionsWithContext(null);
		},
		[]
	);

	const documentUploadHandler = useMemo(
		(): ((files: readonly FileSelectionEntry[]) => void) | undefined => {
			if (!uploadHandlers) {
				return undefined;
			}

			return (files: readonly FileSelectionEntry[]): void => {
				onDocumentUpload(
					files,
					uploadHandlers
				);
			};

		},
		[onDocumentUpload, uploadHandlers]
	);

	const uploadFolderHandler = useCallback(
		(): void => {
			documentSelectRef.current
				?.selectFolder();
		},
		[]
	);

	const uploadFilesHandler = useCallback(
		(): void => {
			documentSelectRef.current
				?.selectFiles();
		},
		[]
	);

	const canUploadDocuments: boolean = folder
		?.permissions
		?.canCreate || false;

	useEffect(
		(): (() => void) | undefined => {
			if (!folder || !canUploadDocuments) {
				return;
			}

			async function getData(folder: Folderish, signal: AbortSignal) {
				try {
					const serverSupportsChunking: boolean = await doesServerSupportChunking(
						convertToLocationModel(folder.location),
						signal
					);

					setDoesSupportChunking(serverSupportsChunking);
				} catch (e) {
					console.error(e);
				}
			}

			const cancellation = new AbortController();
			getData(folder, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			canUploadDocuments,
			folder
		]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			const selectionCount: number = actionsWithContext?.context
				.selection
				.length ?? 0;

			return selectionCount > 0;
		},
		[
			actionsWithContext?.context
				.selection
				.length,
			isMobile
		]
	);

	const isSharedWithMeRoot: boolean = view.type === DocumentsViewType.SharedWithMeView
		&& (folder?.isRoot ?? true);

	const renderSharedWithMeInfo = (): ReactElement => {
		const siteUrl: string = view.location
			.siteUrl;
		const oneDriveUrl: string = new URL(siteUrl).origin + "/shared";

		return (
			<div className={styles.sharedWithMeInfo}>
				<AlertBar
					kind="info"
					isInline
					isDismissable={false}
					trackingId="shared-with-me-info"
				>
					{localize("_SharedItemsOverviewUnavailableInfo")}
					<br />
					<Anchor
						href={oneDriveUrl}
						target="_blank"
						trackingId="open-in-onedrive"
					>
						{localize("_OpenInOneDrive")}
					</Anchor>
				</AlertBar>
			</div>
		);
	};

	return (
		<>
			{folderPath.length > 1 && (
				<TitleBarBackButton
					onBack={() => backHandler(folderPath[folderPath.length - 2])}
				/>
			)}
			<DropZone
				trackingId="drop-zone"
				height="fill"
				isDisabled={!canUploadDocuments}
			>
				<DocumentPageLayout trackingId="sharepoint-page-content">
					<DocumentPageLayout.Header>
						<DocumentPageLayout.BreadCrumb>
							<SharePointBreadCrumb
								items={folderPath}
								onOpenFolder={onOpenFolder}
							/>
						</DocumentPageLayout.BreadCrumb>
						{shouldShowActionBar
							? (
								<DocumentPageLayout.ActionBarContainer>
									<ContextActionBar
										{...(
											actionsWithContext
												? actionsWithContext
												: { actions: EMPTY_ARRAY, context: undefined! /* There are no actions, so no need for a context */ }
										)}
										trackingId="global-action-bar"
										horizontalAlignment={isMobile ? "default" : "page-content"}
										hasBorderBottom={isMobile}
									/>
								</DocumentPageLayout.ActionBarContainer>
							)
							: (
								<PageContentTitle
									{...(
										actionsWithContext
											? { actionContext: actionsWithContext.context, actions: actionsWithContext.actions}
											: { actions: undefined, actionContext: undefined }
									)}
									title={folderPath[folderPath.length - 1].name}
								/>
							)
						}
						{isSharedWithMeRoot
							? renderSharedWithMeInfo()
							: (
								<DocumentPageLayout.SortAndFilter>
									<SharePointSearchForm
										viewKind={viewKind}
										isInRootFolder={folder?.isRoot ?? true}
										value={filter}
										onChange={setFilter}
										isMobile={isMobile}
									/>
									{(isMobile || layoutKind === DocumentLayoutKind.Grid) && (
										<>
											<OptionsMenu
												value={sortingState}
												onChange={setSortingState}
												options={sharePointSortMenuOptions}
												trackingId="sort-menu"
												title={localize("_SortBy")}
												icon="arrow-up-arrow-down"
												buttonSize="large"
											/>
										</>
									)}

									{!isMobile && layoutKind === DocumentLayoutKind.List && (
										<SharePointColumnsVisibility
											isMobile={isMobile}
											value={columnVisibilities}
											onChange={setColumnVisibilities}
											columnsData={columnsVisibilityDefinitions}
										/>
									)}

									<DocumentPageLayout.LayoutKindChangeButton
										layoutKind={layoutKind}
										onLayoutKindChange={onLayoutKindChange}
									/>
								</DocumentPageLayout.SortAndFilter>
							)
						}
					</DocumentPageLayout.Header>

					<DocumentPageLayout.Content>
						{!isSharedWithMeRoot && folder && (
							<SharePointOverView
								view={view}
								folder={folder}
								onOpenFolder={onOpenFolder}
								onFolderPermissionsLoaded={onFolderPermissionsLoaded}
								filter={filter}
								sortingState={sortingState}
								setSortingState={setSortingState}
								isMobile={isMobile}
								isResponsive={isResponsive}
								isSharingViaLinkEnabled={isSharingViaLinkEnabled}
								maxNumberOfFilesToDownloadAsZip={maxNumberOfFilesToDownloadAsZip}
								syncClient={syncClient}
								templateAvailabilityKind={templateAvailabilityKind}
								columnVisibilities={columnVisibilities}
								layoutKind={layoutKind}
								onActionsChanged={actionsChangeHandler}
								onUploadFiles={uploadFilesHandler}
								onUploadFolder={uploadFolderHandler}
							/>
						)}
					</DocumentPageLayout.Content>

					{isNotNil(documentUploadHandler) && canUploadDocuments && (
						<DocumentSelect
							onDocumentSelect={documentUploadHandler}
							ref={documentSelectRef}
						/>
					)}
				</DocumentPageLayout>
			</DropZone>
		</>
	);
};
SharePointPageContent.displayName = "SharePointPageContent";

const sharePointSortMenuOptions: readonly OptionsMenuItem<DataSourceSortableState<GridItem>>[] = [
	{
		text: getPropertySortText("_Name", "_AToZ"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "name-a-z"
	},
	{
		text: getPropertySortText("_Name", "_ZToA"),
		value: { propertyName: "name", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "name-z-a"
	},
	{
		text: getPropertySortText("_DateModified", "_NewToOld"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "modified-on-new-old"
	},
	{
		text: getPropertySortText("_DateModified", "_OldToNew"),
		value: { propertyName: "modifiedOn", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "modified-on-old-new"
	},
	{
		text: getPropertySortText("_Extension", "_AToZ"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "extension-a-z"
	},
	{
		text: getPropertySortText("_Extension", "_ZToA"),
		value: { propertyName: "extension", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "extension-a-z"
	},
	{
		text: getPropertySortText("_FileSize", "_SmallToLarge"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Ascending },
		valueTrackingId: "file-size-small-large"
	},
	{
		text: getPropertySortText("_FileSize", "_LargeToSmall"),
		value: { propertyName: "size", direction: DataSourceSortDirection.Descending },
		valueTrackingId: "file-size-large-small"
	}
];

export type SharePointActionsWithContext = ActionsWithContext<
	SharePointDocumentActionContext,
	ToolbarAction<SharePointDocumentActionContext>
>;
